// ============================================
// Premier Engineer Dashboard Page
// Full-featured with 7 tabs: Dashboard, Case Control, Active Cases, Target Metrics, Corrections, TM Cards, Escalations
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import AppShell from '../layout/AppShell';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';
import type { ActiveCase, CSATStats, IPDMetrics, CaseControlAction, TeamManagerScores } from '../../types';
import { 
  Inbox, BarChart3, CheckCircle2, ArrowUpRight, FolderOpen, RefreshCcw, 
  RotateCw, Lock, Package, Calculator, Briefcase, Star, RotateCcw
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, ArcElement,
  PointElement, LineElement, RadialLinearScale,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, ArcElement,
  PointElement, LineElement, RadialLinearScale,
  Title, Tooltip, Legend, Filler,
);

import { AnimatedStatCard, StatCard, StarRating, TargetCard } from '../dashboard/DashboardWidgets';

const CHART_PALETTE = ['#DC143C', '#FF4060', '#FF6B8A', '#4D96FF', '#00D46A', '#FFB300', '#A855F7', '#14B8A6'];
const chartTooltipStyle = {
  backgroundColor: 'rgba(0,0,0,0.85)', titleColor: '#F0F0F8', bodyColor: '#A0A0B8',
  borderColor: 'rgba(220,20,60,0.4)', borderWidth: 1, cornerRadius: 8, padding: 12,
  titleFont: { weight: '700' as const, size: 13 }, bodyFont: { size: 12 },
};

// --- Sub-components moved to DashboardWidgets ---

// --- Main Component ---

export default function PremierEngineerDashboardPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('premierTab') || 'dashboard');

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    sessionStorage.setItem('premierTab', tab);
  };

  // Dashboard stats
  const [metrics, setMetrics] = useState<any>(null);

  // Case Control
  const [closureModal, setClosureModal] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [archiveModal, setArchiveModal] = useState(false);

  // Case Control form state
  const [closureForm, setClosureForm] = useState({ caseNumber: '', closureType: 'issue_resolved', notes: '' });
  const [assignForm, setAssignForm] = useState({ caseNumber: '', assignedBy: 'que_manager', caseType: ['fresh_case'] as string[], notes: '' });
  const [transferForm, setTransferForm] = useState({ caseNumber: '', sapReference: '', smeNoteAdded: false, targetLOB: '', confirmed: false, receivedSameMonth: false });

  // Active Cases
  const [activeCases, setActiveCases] = useState<ActiveCase[]>([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [caseSearch, setCaseSearch] = useState('');
  const [activeCaseOverlayData, setActiveCaseOverlayData] = useState<string | null>(null);

  // Archive
  const [archiveActions, setArchiveActions] = useState<CaseControlAction[]>([]);
  const [archiveMonth, setArchiveMonth] = useState(new Date().getMonth() + 1);
  const [archiveYear, setArchiveYear] = useState(new Date().getFullYear());
  const [archiveLoading, setArchiveLoading] = useState(false);

  // Target Metrics
  const [csatStats, setCsatStats] = useState<CSATStats | null>(null);
  const [ipdMetrics, setIpdMetrics] = useState<IPDMetrics | null>(null);
  const [ipdMonth, setIpdMonth] = useState(new Date().toISOString().slice(0, 7));
  const [surveyModal, setSurveyModal] = useState(false);
  const [surveyRating, setSurveyRating] = useState(0);
  const [calcModal, setCalcModal] = useState(false);
  const [calcInputs, setCalcInputs] = useState({ n1: '', n2: '', n3: '', n4: '', n5: '' });
  const [calcResult, setCalcResult] = useState<{ average: number; topBox: number; csat: number } | null>(null);
  const [backlogModal, setBacklogModal] = useState(false);
  const [backlogForm, setBacklogForm] = useState({ monthKey: ipdMonth, startingBacklog: '' });

  // Corrections
  const [recentActions, setRecentActions] = useState<CaseControlAction[]>([]);
  const [correctCsatModal, setCorrectCsatModal] = useState(false);
  const [csatCorrectForm, setCsatCorrectForm] = useState({ n1: '', n2: '', n3: '', n4: '', n5: '' });
  const [loadBacklogModal, setLoadBacklogModal] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [loadBacklogForm, setLoadBacklogForm] = useState({
    startingBacklog: '', casesAssigned: '', casesClosed: '',
    transfers: [{ count: '', receivedSameMonth: false }] as { count: string | number; receivedSameMonth: boolean }[],
  });

  // TM Cards
  const [tmScores, setTmScores] = useState<TeamManagerScores | null>(null);

  // Escalations
  const [peEscalations, setPeEscalations] = useState<any[]>([]);
  const [escLoading, setEscLoading] = useState(false);
  const [newEscModal, setNewEscModal] = useState(false);
  const [escForm, setEscForm] = useState({ caseNumber: '', description: '' });
  const [expandedEscId, setExpandedEscId] = useState<string | null>(null);
  const [escThread, setEscThread] = useState<any>(null);
  const [escReply, setEscReply] = useState('');

  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<{ type: string; message: string } | null>(null);

  // Scroll to top when any modal opens
  useEffect(() => {
    const anyModalOpen = closureModal || assignModal || transferModal || archiveModal || 
      activeCaseOverlayData || surveyModal || calcModal || backlogModal || 
      correctCsatModal || loadBacklogModal || newEscModal;
    if (anyModalOpen) {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [closureModal, assignModal, transferModal, archiveModal, activeCaseOverlayData, surveyModal, calcModal, backlogModal, correctCsatModal, loadBacklogModal, newEscModal]);

  const showToast = (type: string, message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const loadMetrics = useCallback(async () => {
    try {
      const data = await api.get<any>(`/case-control/metrics?monthKey=${ipdMonth}`);
      setMetrics(data);
    } catch { }
  }, [ipdMonth]);

  const loadActiveCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const params = caseSearch ? `?search=${caseSearch}` : '';
      const data = await api.get<any>(`/case-control/active-cases${params}`);
      setActiveCases(data.cases || []);
    } catch { }
    setCasesLoading(false);
  }, [caseSearch]);

  const loadCsatStats = useCallback(async () => {
    try {
      const data = await api.get<any>('/csat/stats');
      setCsatStats(data);
    } catch { }
  }, []);

  const loadIpdMetrics = useCallback(async () => {
    try {
      const data = await api.get<any>(`/ipd/metrics?monthKey=${ipdMonth}`);
      setIpdMetrics(data);
    } catch { }
  }, [ipdMonth]);

  const loadRecentActions = useCallback(async () => {
    try {
      const data = await api.get<any>('/case-control/recent-actions?limit=20');
      setRecentActions(data.actions || []);
    } catch { }
  }, []);

  const loadArchive = useCallback(async () => {
    setArchiveLoading(true);
    try {
      const data = await api.get<any>(`/case-control/archive?month=${archiveMonth}&year=${archiveYear}&limit=100`);
      setArchiveActions(data.actions || []);
    } catch { }
    setArchiveLoading(false);
  }, [archiveMonth, archiveYear]);

  const loadTmScores = useCallback(async () => {
    try {
      const data = await api.get<any>('/team/premier-engineer/team-manager-scores');
      setTmScores(data);
    } catch { }
  }, []);

  const loadPeEscalations = useCallback(async () => {
    setEscLoading(true);
    try {
      const data = await api.get<any>('/escalations');
      setPeEscalations(data.escalations || []);
    } catch { }
    setEscLoading(false);
  }, []);

  const loadEscThread = async (id: string) => {
    try {
      const data = await api.get<any>(`/escalations/${id}`);
      setEscThread(data);
      setExpandedEscId(id);
    } catch { }
  };

  // Use effect on mount is handled by the activeTab watcher

  useEffect(() => {
    if (activeTab === 'dashboard') { loadMetrics(); loadCsatStats(); }
    if (activeTab === 'active-cases') loadActiveCases();
    if (activeTab === 'target-metrics') { loadCsatStats(); loadIpdMetrics(); }
    if (activeTab === 'corrections') loadRecentActions();
    if (activeTab === 'team-manager-cards') loadTmScores();
    if (activeTab === 'escalations') loadPeEscalations();
  }, [activeTab]);

  // --- Actions ---
  const refreshCurrentTab = () => {
    if (activeTab === 'dashboard') { loadMetrics(); loadCsatStats(); }
    if (activeTab === 'active-cases') loadActiveCases();
    if (activeTab === 'target-metrics') { loadCsatStats(); loadIpdMetrics(); }
    if (activeTab === 'corrections') loadRecentActions();
    if (activeTab === 'team-manager-cards') loadTmScores();
    if (activeTab === 'escalations') loadPeEscalations();
    if (archiveModal) loadArchive();
  };

  const submitClosure = async () => {
    setActionLoading(true);
    try {
      await api.post('/case-control/closure', closureForm);
      showToast('success', `Case ${closureForm.caseNumber} closed`);
      setClosureModal(false);
      setClosureForm({ caseNumber: '', closureType: 'issue_resolved', notes: '' });
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
    setActionLoading(false);
  };

  const submitAssignment = async () => {
    setActionLoading(true);
    try {
      await api.post('/case-control/assignment', assignForm);
      showToast('success', `Case ${assignForm.caseNumber} assigned`);
      setAssignModal(false);
      setAssignForm({ caseNumber: '', assignedBy: 'que_manager', caseType: ['fresh_case'], notes: '' });
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
    setActionLoading(false);
  };

  const submitTransfer = async () => {
    setActionLoading(true);
    try {
      await api.post('/case-control/transfer', transferForm);
      showToast('success', `Case ${transferForm.caseNumber} transferred`);
      setTransferModal(false);
      setTransferForm({ caseNumber: '', sapReference: '', smeNoteAdded: false, targetLOB: '', confirmed: false, receivedSameMonth: false });
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
    setActionLoading(false);
  };

  const submitReopen = async (caseNumber: string) => {
    try {
      await api.post('/case-control/reopen', { caseNumber });
      showToast('success', `Case ${caseNumber} reopened`);
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
  };

  const submitBounceBack = async (caseNumber: string) => {
    try {
      await api.post('/case-control/bounce-back', { caseNumber });
      showToast('success', `Case ${caseNumber} bounced back`);
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
  };

  const submitSurvey = async () => {
    if (surveyRating < 1) return;
    try {
      await api.post('/csat/rate', { rating: surveyRating });
      showToast('success', `${surveyRating}-star rating logged`);
      setSurveyModal(false);
      setSurveyRating(0);
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
  };

  const calculateCsat = async () => {
    try {
      const { n1, n2, n3, n4, n5 } = calcInputs;
      const data = await api.get<any>(`/csat/calculate?n1=${n1}&n2=${n2}&n3=${n3}&n4=${n4}&n5=${n5}`);
      setCalcResult(data);
    } catch { }
  };

  const resetCsat = async () => {
    if (!confirm('Reset all CSAT survey data? This cannot be undone.')) return;
    try {
      await api.delete('/csat/reset', { confirmed: true });
      showToast('success', 'CSAT data reset');
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
  };

  const setStartingBacklog = async () => {
    try {
      await api.post('/ipd/backlog', backlogForm);
      showToast('success', 'Starting backlog set');
      setBacklogModal(false);
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
  };

  const undoAction = async (actionId: string) => {
    if (!confirm('Undo this action?')) return;
    try {
      await api.post(`/case-control/undo/${actionId}`, { confirmed: true });
      showToast('success', 'Action undone');
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
  };

  const correctCsat = async () => {
    try {
      await api.put('/csat/correct', csatCorrectForm);
      showToast('success', 'CSAT corrected');
      setCorrectCsatModal(false);
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
  };

  const loadBacklog = async () => {
    setActionLoading(true);
    try {
      await api.post('/case-control/load-backlog', { ...loadBacklogForm, isReset: isResetMode });
      showToast('success', isResetMode ? 'Backlog reset successfully' : 'Backlog loaded successfully');
      setLoadBacklogModal(false);
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
    setActionLoading(false);
  };

  const undoBacklog = async () => {
    if (!confirm('Undo all backlog-loaded actions? CSAT data will NOT be affected.')) return;
    try {
      await api.delete('/case-control/backlog', { confirmed: true });
      showToast('success', 'Backlog undone (CSAT unaffected)');
      refreshCurrentTab();
    } catch (e: any) { showToast('error', e.message); }
  };

  const carryOverBacklog = async () => {
    setActionLoading(true);
    try {
      const data = await api.post<any>('/ipd/carry-over');
      if (data.alreadySet) {
        showToast('success', `Backlog already set for ${data.monthKey}: ${data.startingBacklog} cases`);
      } else {
        showToast('success', data.message);
      }
      loadIpdMetrics();
    } catch (e: any) { showToast('error', e.message); }
    setActionLoading(false);
  };

  const toggleCaseType = (type: string) => {
    setAssignForm((f) => ({
      ...f,
      caseType: f.caseType.includes(type) ? f.caseType.filter((t) => t !== type) : [...f.caseType, type],
    }));
  };

  // --- Tab Renderers ---

  // Chart data
  const actionDoughnutData = {
    labels: ['Assigned', 'Closed', 'Transferred', 'Reopened', 'Bounced'],
    datasets: [{
      data: [
        metrics?.totalAssigned || 0,
        metrics?.closed || 0,
        metrics?.transferred || 0,
        metrics?.customerReopened || 0,
        metrics?.bouncedBack || 0,
      ],
      backgroundColor: ['#4D96FFCC', '#00D46ACC', '#FFB300CC', '#A855F7CC', '#DC143CCC'],
      borderColor: 'transparent', hoverOffset: 10,
    }],
  };

  const closureBarData = {
    labels: ['Issue Resolved', 'Strike Process', 'OOB Intune'],
    datasets: [{
      label: 'Closures',
      data: [
        metrics?.closureBreakdown?.issue_resolved || metrics?.closed || 0,
        metrics?.closureBreakdown?.strike_process || 0,
        metrics?.closureBreakdown?.oob_for_intune || 0,
      ],
      backgroundColor: ['#00D46ACC', '#4D96FFCC', '#FFB300CC'],
      borderColor: ['#00D46A', '#4D96FF', '#FFB300'],
      borderWidth: 1, borderRadius: 6, borderSkipped: false as const,
    }],
  };

  // CSAT chart data for dashboard
  const csatDoughnutData = {
    labels: ['★5', '★4', '★3', '★2', '★1'],
    datasets: [{
      data: csatStats ? [csatStats.ratingBreakdown[5]||0, csatStats.ratingBreakdown[4]||0, csatStats.ratingBreakdown[3]||0, csatStats.ratingBreakdown[2]||0, csatStats.ratingBreakdown[1]||0] : [0,0,0,0,0],
      backgroundColor: ['#00D46ACC', '#80E060CC', '#FFB300CC', '#FF6830CC', '#FF3D3DCC'],
      borderColor: 'transparent', hoverOffset: 8,
    }],
  };

  const renderDashboard = () => (
    <>
      <div className="grid-stats" style={{ marginBottom: 'var(--space-6)' }}>
        <AnimatedStatCard label="Total Assigned" value={metrics?.totalAssigned || 0} icon={<Inbox size={20} />} color="#4D96FF" delay={0} />
        <AnimatedStatCard label="Closed" value={metrics?.closed || 0} icon={<CheckCircle2 size={20} />} color="#00D46A" delay={80} />
        <AnimatedStatCard label="Transferred" value={metrics?.transferred || 0} icon={<ArrowUpRight size={20} />} color="#FFB300" delay={160} />
        <AnimatedStatCard label="Active Cases" value={metrics?.activeCases || 0} icon={<FolderOpen size={20} />} color="#DC143C" delay={240} />
      </div>
      <div className="grid-stats" style={{ marginBottom: 'var(--space-6)' }}>
        <AnimatedStatCard label="Net Assignment" value={metrics?.netAssigned || 0} icon={<BarChart3 size={20} />} color="#A855F7" delay={320} />
        <AnimatedStatCard label="Closure Rate" value={metrics?.closureRate || 0} suffix="%" icon={<BarChart3 size={20} />} color="#14B8A6" delay={400} />
        <AnimatedStatCard label="CSAT %" value={csatStats?.csatPercentage || 0} suffix="%" icon={<Star size={20} />} color="#FFD700" delay={480} />
        <AnimatedStatCard label="CSAT Avg" value={csatStats?.averageRating || 0} icon={<Star size={20} />} color="#FF6B8A" delay={560} />
      </div>

       <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-5)' }}>
        <div className="glass-card animate-float-in" style={{ padding: 'var(--space-5)', animationDelay: '600ms', animationFillMode: 'both' }}>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Case Actions</h3>
          <div style={{ position: 'relative', maxHeight: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Doughnut data={actionDoughnutData} options={{
              responsive: true, maintainAspectRatio: true, cutout: '60%',
              plugins: {
                legend: { position: 'bottom', labels: { color: '#A0A0B8', padding: 12, font: { size: 11, family: 'Inter', weight: 'bold' }, usePointStyle: true, pointStyleWidth: 10 } },
                tooltip: chartTooltipStyle as any,
              },
              animation: { animateRotate: true, animateScale: true, duration: 1600, easing: 'easeOutQuart' },
            }} />
          </div>
        </div>

        <div className="glass-card animate-float-in" style={{ padding: 'var(--space-5)', animationDelay: '700ms', animationFillMode: 'both' }}>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Closure Breakdown</h3>
          <div style={{ position: 'relative', maxHeight: '260px' }}>
            <Bar data={closureBarData} options={{
              responsive: true, maintainAspectRatio: true,
              plugins: { legend: { display: false }, tooltip: chartTooltipStyle as any },
              scales: {
                x: { grid: { color: 'rgba(139,0,0,0.08)' }, ticks: { color: '#A0A0B8', font: { size: 11, family: 'Inter' } }, border: { color: 'rgba(139,0,0,0.2)' } },
                y: { beginAtZero: true, grid: { color: 'rgba(139,0,0,0.08)' }, ticks: { color: '#A0A0B8', font: { size: 11, family: 'Inter' }, stepSize: 1 }, border: { color: 'rgba(139,0,0,0.2)' } },
              },
              animation: { duration: 1400, easing: 'easeOutQuart' },
            }} />
          </div>
        </div>

        <div className="glass-card animate-float-in" style={{ padding: 'var(--space-5)', animationDelay: '800ms', animationFillMode: 'both' }}>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>CSAT Ratings</h3>
          <div style={{ position: 'relative', maxHeight: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Doughnut data={csatDoughnutData} options={{
              responsive: true, maintainAspectRatio: true, cutout: '55%',
              plugins: {
                legend: { position: 'bottom', labels: { color: '#A0A0B8', padding: 10, font: { size: 10, family: 'Inter', weight: 'bold' }, usePointStyle: true, pointStyleWidth: 8 } },
                tooltip: chartTooltipStyle as any,
              },
              animation: { animateRotate: true, animateScale: true, duration: 1600, easing: 'easeOutQuart' },
            }} />
          </div>
          {csatStats && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Top Box</div>
                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 800, color: 'var(--accent-primary)' }}>{csatStats.topBoxPercentage}%</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>CSat</div>
                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 800, color: 'var(--success)' }}>{csatStats.csatPercentage}%</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Surveys</div>
                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 800 }}>{csatStats.totalResponses}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  const renderCaseControl = () => (
    <div className="grid-cards">
      <div className="control-card" onClick={() => { setClosureForm({ caseNumber: '', closureType: 'issue_resolved', notes: '' }); setClosureModal(true); }}>
        <span className="control-card__icon"><Lock size={24} /></span>
        <div className="control-card__title">Closure</div>
        <div className="control-card__desc">Issue Resolved · Strike Process · OOB for Intune</div>
      </div>
      <div className="control-card" onClick={() => { setAssignForm({ caseNumber: '', assignedBy: 'que_manager', caseType: ['fresh_case'], notes: '' }); setAssignModal(true); }}>
        <span className="control-card__icon"><Inbox size={24} /></span>
        <div className="control-card__title">Assignment</div>
        <div className="control-card__desc">QUE Manager · BOT · Fresh / Received / Expired SLA</div>
      </div>
      <div className="control-card" onClick={() => { setTransferForm({ caseNumber: '', sapReference: '', smeNoteAdded: false, targetLOB: '', confirmed: false, receivedSameMonth: false }); setTransferModal(true); }}>
        <span className="control-card__icon"><ArrowUpRight size={24} /></span>
        <div className="control-card__title">Transfer</div>
        <div className="control-card__desc">SAP Reference · SME Note · Target LOB · Confirmation</div>
      </div>
      <div className="control-card" onClick={() => { setArchiveModal(true); loadArchive(); }}>
        <span className="control-card__icon"><Package size={24} /></span>
        <div className="control-card__title">Archive</div>
        <div className="control-card__desc">View history · Mark Reopened · Mark Bounced Back</div>
      </div>
    </div>
  );

  const renderActiveCases = () => (
    <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
      <div className="flex-between" style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>Active Cases</h2>
        <input className="glass-input" style={{ maxWidth: 300, flex: '1 1 200px' }} placeholder="Search case number..."
          value={caseSearch} onChange={(e) => setCaseSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadActiveCases()} />
      </div>

      {casesLoading ? (
        <div className="flex-center" style={{ padding: 'var(--space-8)' }}><div className="spinner" /></div>
      ) : activeCases.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">📂</div>
          <div className="empty-state__title">No active cases</div>
          <div className="empty-state__text">Assigned cases will appear here.</div>
        </div>
      ) : (
        <div className="glass-table-wrapper">
          <table className="glass-table">
            <thead><tr>
              <th>Case Number</th><th>Assigned By</th><th>Type</th><th>Received</th><th>Status</th>
            </tr></thead>
            <tbody>
              {activeCases.map((c) => (
                <tr key={c.actionId} onClick={() => setActiveCaseOverlayData(c.caseNumber)} style={{ cursor: 'pointer' }}>
                  <td data-label="Case Number" style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{c.caseNumber}</td>
                  <td data-label="Assigned By"><span className="glass-badge glass-badge--open">{c.assignedBy || '—'}</span></td>
                  <td data-label="Type" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                    {Array.isArray(c.caseType) ? c.caseType.join(', ').replace(/_/g, ' ') : '—'}
                  </td>
                  <td data-label="Received" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {new Date(c.receivedAt).toLocaleDateString()}
                  </td>
                  <td data-label="Status"><span className="glass-badge glass-badge--in_progress">{c.status.replace(/_/g, ' ')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderTargetMetrics = () => (
    <>
      {/* CSAT Section */}
      <div className="glass-card" style={{ padding: 'var(--space-5)', marginBottom: 'var(--space-5)' }}>
        <div className="flex-between" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>📊 CSAT</h2>
          <div className="flex-gap-2">
            <button className="glass-btn glass-btn--primary glass-btn--sm" onClick={() => setSurveyModal(true)}>
              ⭐ I got a survey!
            </button>
            <button className="glass-btn glass-btn--secondary glass-btn--sm" onClick={() => setCalcModal(true)}>
              🧮 Calculator
            </button>
            <button className="glass-btn glass-btn--ghost glass-btn--sm" onClick={resetCsat} style={{ color: 'var(--error)' }}>
              Reset
            </button>
          </div>
        </div>

        {csatStats ? (
          <div className="grid-stats">
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>Average</div>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 800 }}>{csatStats.averageRating}</div>
            </div>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>Top Box %</div>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 800, color: 'var(--accent-primary)' }}>{csatStats.topBoxPercentage}%</div>
            </div>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>CSat %</div>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 800, color: 'var(--success)' }}>{csatStats.csatPercentage}%</div>
            </div>
            <div className="glass-card" style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>Surveys</div>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 800 }}>{csatStats.totalResponses}</div>
            </div>
          </div>
        ) : (
          <div className="flex-center" style={{ padding: 'var(--space-8)' }}><div className="spinner" /></div>
        )}

        {/* Rating breakdown bar */}
        {csatStats && csatStats.totalResponses > 0 && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            {[5, 4, 3, 2, 1].map((star) => {
              const count = csatStats.ratingBreakdown[star] || 0;
              const pct = csatStats.totalResponses > 0 ? (count / csatStats.totalResponses) * 100 : 0;
              return (
                <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
                  <span style={{ width: 30, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>★{star}</span>
                  <div style={{ flex: 1, height: 8, background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-pill)', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: star >= 4 ? 'var(--success)' : star === 3 ? 'var(--warning)' : 'var(--error)', borderRadius: 'var(--radius-pill)', transition: 'width 500ms ease' }} />
                  </div>
                  <span style={{ width: 36, fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', textAlign: 'right' }}>{count}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* IPD Section */}
      <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
        <div className="flex-between" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>📈 IPD Metrics</h2>
          <div className="flex-gap-2" style={{ alignItems: 'center' }}>
            <button className="glass-btn glass-btn--secondary glass-btn--sm" onClick={carryOverBacklog} disabled={actionLoading}
              title="Automatically calculate last month's ending backlog as this month's starting backlog">
              📦 Carry Over Backlog
            </button>
            <input type="month" className="glass-input" style={{ maxWidth: 180 }} value={ipdMonth}
              onChange={(e) => { setIpdMonth(e.target.value); }} />
          </div>
        </div>

        {ipdMetrics ? (
          <>
            <div className="grid-stats" style={{ marginBottom: 'var(--space-4)' }}>
              <StatCard label="Backlog" value={ipdMetrics.startingBacklog} icon={<Package size={20} />} />
              <StatCard label="Assigned" value={ipdMetrics.totalAssigned} icon={<Inbox size={20} />} />
              <StatCard label="Closed" value={ipdMetrics.totalClosed} icon={<CheckCircle2 size={20} />} />
              <StatCard label="Transferred" value={ipdMetrics.totalTransferred} icon={<ArrowUpRight size={20} />} />
              <StatCard label="Net Assigned" value={ipdMetrics.netAssigned} icon={<BarChart3 size={20} />} />
              <StatCard label="Active Cases" value={ipdMetrics.currentBacklog} icon={<FolderOpen size={20} />} accent />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <TargetCard num={1} label="Active cases = Backlog − 1"
                goal={ipdMetrics.target1.goal} met={ipdMetrics.target1.met} remaining={ipdMetrics.target1.remaining} />
              <TargetCard num={2} label="Closure = Net Assigned + 1"
                goal={ipdMetrics.target2.goal} met={ipdMetrics.target2.met} remaining={ipdMetrics.target2.remaining} />
            </div>
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-state__icon">📈</div>
            <div className="empty-state__title">No IPD data</div>
            <div className="empty-state__text">Set a starting backlog to begin tracking.</div>
          </div>
        )}
      </div>
    </>
  );

  const renderCorrections = () => (
    <>
      <div className="grid-cards" style={{ marginBottom: 'var(--space-5)' }}>
        <div className="glass-card glass-card--hover" style={{ padding: 'var(--space-5)', cursor: 'pointer' }}
          onClick={() => { setCsatCorrectForm({ n1: '', n2: '', n3: '', n4: '', n5: '' }); setCorrectCsatModal(true); }}>
          <div style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}><BarChart3 size={28} /></div>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-1)' }}>Correct CSAT Score</h3>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>Replace CSAT with manual n1-n5 counts</p>
        </div>

        <div className="glass-card glass-card--hover" style={{ padding: 'var(--space-5)', cursor: 'pointer' }}
          onClick={() => { setLoadBacklogForm({ startingBacklog: '', casesAssigned: '', casesClosed: '', transfers: [{ count: '', receivedSameMonth: false }] }); setIsResetMode(false); setLoadBacklogModal(true); }}>
          <div style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}><Package size={28} /></div>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-1)' }}>Load Backlog</h3>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>Bulk load previous month's data</p>
        </div>

        <div className="glass-card glass-card--hover" style={{ padding: 'var(--space-5)', cursor: 'pointer' }}
          onClick={() => { setLoadBacklogForm({ startingBacklog: '', casesAssigned: '', casesClosed: '', transfers: [{ count: '', receivedSameMonth: false }] }); setIsResetMode(true); setLoadBacklogModal(true); }}>
          <div style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}><RotateCcw size={28} /></div>
          <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-1)' }}>Reset Backlog</h3>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>Clear logged data completely and set new manually</p>
        </div>
      </div>

      {/* Recent Actions */}
      <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>
          Correct Logged Actions (Recent 20)
        </h2>
        {recentActions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon">📋</div>
            <div className="empty-state__title">No actions yet</div>
          </div>
        ) : (
          <div className="glass-table-wrapper">
            <table className="glass-table">
              <thead><tr>
                <th>Date</th><th>Case #</th><th>Action</th><th>Details</th><th>Backlog</th><th>Undo</th>
              </tr></thead>
              <tbody>
                {recentActions.map((a) => (
                  <tr key={a.id}>
                    <td data-label="Date" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                      {new Date(a.createdAt).toLocaleString()}
                    </td>
                    <td data-label="Case #" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-xs)' }}>{a.caseNumber}</td>
                    <td data-label="Action"><span className="glass-badge glass-badge--open">{a.actionType.replace(/_/g, ' ')}</span></td>
                    <td data-label="Details" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                      {a.closureType || a.targetLOB || (a.caseType ? a.caseType.join(', ') : '') || '—'}
                    </td>
                    <td data-label="Backlog">{a.isBacklogLoaded ? <span className="glass-badge glass-badge--in_progress">Backlog</span> : '—'}</td>
                    <td data-label="Undo">
                      <button className="glass-btn glass-btn--ghost glass-btn--sm" style={{ color: 'var(--error)' }}
                        onClick={() => undoAction(a.id)}>
                        Undo
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  const renderTmCards = () => (
    <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
      <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>
        💼 Team Manager Cards
      </h2>
      {!tmScores?.managerId ? (
        <div className="empty-state">
          <div className="empty-state__icon">💼</div>
          <div className="empty-state__title">No communicated scores</div>
          <div className="empty-state__text">Your Team Leader hasn't sent any score communications yet.</div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
            From: <strong>{tmScores.managerName}</strong> • Last updated: {tmScores.lastUpdated ? new Date(tmScores.lastUpdated).toLocaleDateString() : '—'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-3)' }}>📈 IPD</h3>
              <div className="flex-gap-4" style={{ flexWrap: 'wrap' }}>
                <div><span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Backlog</span>
                  <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 800 }}>{tmScores.ipd?.startingBacklog || 0}</div></div>
                <div><span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Net Assigned</span>
                  <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 800 }}>{tmScores.ipd?.casesAssigned || 0}</div></div>
                <div><span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Closed</span>
                  <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 800 }}>{tmScores.ipd?.casesClosed || 0}</div></div>
              </div>
            </div>
            <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700, marginBottom: 'var(--space-3)' }}>📊 CSAT</h3>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 800, color: 'var(--accent-primary)' }}>
                {tmScores.csat?.csatPercentage || 0}%
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
                {tmScores.csat?.totalResponses || 0} total surveys
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const submitEscalation = async () => {
    if (!escForm.caseNumber || !escForm.description) return;
    setActionLoading(true);
    try {
      await api.post('/escalations', escForm);
      showToast('success', `Escalation created for case ${escForm.caseNumber}`);
      setNewEscModal(false);
      setEscForm({ caseNumber: '', description: '' });
      loadPeEscalations();
    } catch (e: any) { showToast('error', e.message); }
    setActionLoading(false);
  };

  const sendEscReply = async (escalationId: string) => {
    if (!escReply.trim()) return;
    try {
      await api.post(`/escalations/${escalationId}/messages`, { message: escReply });
      setEscReply('');
      showToast('success', 'Reply sent');
      loadEscThread(escalationId);
      loadPeEscalations();
    } catch (e: any) { showToast('error', e.message); }
  };

  const statusColors: Record<string, string> = { open: 'critical', see_discussion: 'in_progress', closed: 'success' };

  const renderEscalations = () => (
    <>
      <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
        <div className="flex-between" style={{ marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>🔺 My Escalations</h2>
          <div className="flex-gap-2">
            <button className="glass-btn glass-btn--secondary glass-btn--sm" onClick={loadPeEscalations}>Refresh</button>
            <button className="glass-btn glass-btn--primary glass-btn--sm" onClick={() => setNewEscModal(true)}>+ New Escalation</button>
          </div>
        </div>

        {escLoading ? (
          <div className="flex-center" style={{ padding: 'var(--space-8)' }}><div className="spinner" /></div>
        ) : peEscalations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon">✅</div>
            <div className="empty-state__title">No escalations</div>
            <div className="empty-state__text">Create an escalation when you need SME assistance on a case.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {peEscalations.map((esc) => (
              <div key={esc.id} className="glass-card glass-card--hover" style={{ padding: 'var(--space-4)', cursor: 'pointer' }}
                onClick={() => expandedEscId === esc.id ? setExpandedEscId(null) : loadEscThread(esc.id)}>
                <div className="flex-between" style={{ marginBottom: 'var(--space-2)' }}>
                  <div style={{ fontWeight: 700 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-xs)', marginRight: 'var(--space-2)' }}>
                      {esc.caseNumber}
                    </span>
                    <span className={`glass-badge glass-badge--${statusColors[esc.status] || 'open'}`}>
                      {esc.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {new Date(esc.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                  {esc.description}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  SME: {esc.smeName}
                  {esc.lastMessage && <> • Last: "{esc.lastMessage.substring(0, 50)}{esc.lastMessage.length > 50 ? '...' : ''}"</>}
                </div>

                {expandedEscId === esc.id && escThread && (
                  <div style={{ marginTop: 'var(--space-4)', borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}
                    onClick={(e) => e.stopPropagation()}>
                    <div style={{ maxHeight: '250px', overflowY: 'auto', marginBottom: 'var(--space-3)' }}>
                      {escThread.messages?.map((msg: any) => (
                        <div key={msg.id} style={{
                          padding: 'var(--space-3)', marginBottom: 'var(--space-2)',
                          background: msg.senderRole === 'sme' ? 'rgba(77, 150, 255, 0.08)' : 'rgba(220, 20, 60, 0.06)',
                          borderRadius: 'var(--radius-md)', borderLeft: `3px solid ${msg.senderRole === 'sme' ? '#4D96FF' : '#DC143C'}`,
                        }}>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
                            <strong>{msg.senderName}</strong> ({msg.senderRole}) • {new Date(msg.createdAt).toLocaleString()}
                          </div>
                          <div style={{ fontSize: 'var(--font-size-sm)' }}>{msg.message}</div>
                        </div>
                      ))}
                      {escThread.messages?.length === 0 && (
                        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 'var(--space-4)', fontSize: 'var(--font-size-sm)' }}>
                          No messages yet — waiting for SME response
                        </div>
                      )}
                    </div>

                    {esc.status !== 'closed' && (
                      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <input className="glass-input" style={{ flex: 1 }} placeholder="Type a reply..."
                          value={escReply} onChange={(e) => setEscReply(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && sendEscReply(esc.id)} />
                        <button className="glass-btn glass-btn--primary glass-btn--sm" onClick={() => sendEscReply(esc.id)} disabled={!escReply.trim()}>
                          Send
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Escalation Modal */}
      {newEscModal && (
        <div className="glass-modal-overlay" onClick={() => setNewEscModal(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">🔺 New Escalation</span>
              <button className="glass-modal__close" onClick={() => setNewEscModal(false)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Case Number</label>
                <input className="glass-input" value={escForm.caseNumber}
                  onChange={(e) => setEscForm({ ...escForm, caseNumber: e.target.value })} placeholder="Enter case number" autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="glass-input" rows={3} value={escForm.description}
                  onChange={(e) => setEscForm({ ...escForm, description: e.target.value })}
                  placeholder="Describe what you need help with..." style={{ resize: 'vertical', minHeight: '80px' }} />
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setNewEscModal(false)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={submitEscalation}
                disabled={actionLoading || !escForm.caseNumber || !escForm.description}>
                {actionLoading ? 'Creating...' : 'Create Escalation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'case-control': return renderCaseControl();
      case 'active-cases': return renderActiveCases();
      case 'target-metrics': return renderTargetMetrics();
      case 'corrections': return renderCorrections();
      case 'team-manager-cards': return renderTmCards();
      case 'escalations': return renderEscalations();
      default: return renderDashboard();
    }
  };

  return (
    <AppShell role="premier_engineer" activeTab={activeTab} onTabChange={handleTabChange}>
      {renderContent()}

      {/* Toast */}
      {toast && (
        <div className="glass-toast-container">
          <div className={`glass-toast glass-toast--${toast.type}`}>
            {toast.type === 'success' ? '✅' : '❌'} {toast.message}
          </div>
        </div>
      )}

      {/* Active Case Action Overlay Modal */}
      {activeCaseOverlayData && (
        <div className="glass-modal-overlay" onClick={() => setActiveCaseOverlayData(null)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">🛠️ Manage Case {activeCaseOverlayData}</span>
              <button className="glass-modal__close" onClick={() => setActiveCaseOverlayData(null)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div className="grid-cards">
                <div className="control-card" onClick={() => {
                  setClosureForm({ ...closureForm, caseNumber: activeCaseOverlayData });
                  setActiveCaseOverlayData(null);
                  setClosureModal(true);
                }}>
                  <span className="control-card__icon">🔒</span>
                  <div className="control-card__title">Closure</div>
                  <div className="control-card__desc">Issue Resolved · Strike Process · OOB for Intune</div>
                </div>
                <div className="control-card" onClick={() => {
                  setTransferForm({ ...transferForm, caseNumber: activeCaseOverlayData });
                  setActiveCaseOverlayData(null);
                  setTransferModal(true);
                }}>
                  <span className="control-card__icon">↗️</span>
                  <div className="control-card__title">Transfer</div>
                  <div className="control-card__desc">SAP Reference · SME Note · Target LOB · Confirmation</div>
                </div>
                <div className="control-card" onClick={() => {
                  const cn = activeCaseOverlayData;
                  setEscForm({ caseNumber: cn, description: '' });
                  setActiveCaseOverlayData(null);
                  handleTabChange('escalations');
                  setTimeout(() => setNewEscModal(true), 350);
                }}>
                  <span className="control-card__icon">🔺</span>
                  <div className="control-card__title">Escalate</div>
                  <div className="control-card__desc">Escalate to SME · Pre-filled case number · Needs description</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Closure Modal */}
      {closureModal && (
        <div className="glass-modal-overlay" onClick={() => setClosureModal(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">🔒 Close Case</span>
              <button className="glass-modal__close" onClick={() => setClosureModal(false)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Case Number</label>
                <input className="glass-input" value={closureForm.caseNumber}
                  onChange={(e) => setClosureForm({ ...closureForm, caseNumber: e.target.value })} placeholder="Enter case number" autoFocus />
              </div>
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Closure Type</label>
                <select className="glass-select" value={closureForm.closureType}
                  onChange={(e) => setClosureForm({ ...closureForm, closureType: e.target.value })}>
                  <option value="issue_resolved">Issue Resolved</option>
                  <option value="strike_process">Strike Process</option>
                  <option value="oob_for_intune">OOB for Intune</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <input className="glass-input" value={closureForm.notes}
                  onChange={(e) => setClosureForm({ ...closureForm, notes: e.target.value })} placeholder="Additional notes" />
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setClosureModal(false)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={submitClosure} disabled={actionLoading || !closureForm.caseNumber}>
                {actionLoading ? 'Closing...' : 'Close Case'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {assignModal && (
        <div className="glass-modal-overlay" onClick={() => setAssignModal(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">📥 New Assignment</span>
              <button className="glass-modal__close" onClick={() => setAssignModal(false)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Case Number</label>
                <input className="glass-input" value={assignForm.caseNumber}
                  onChange={(e) => setAssignForm({ ...assignForm, caseNumber: e.target.value })} placeholder="e.g., 2603300050000658" autoFocus />
              </div>
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Assigned By</label>
                <div className="flex-gap-4">
                  <label className="glass-radio">
                    <input type="radio" name="assignedBy" value="que_manager" checked={assignForm.assignedBy === 'que_manager'}
                      onChange={() => setAssignForm({ ...assignForm, assignedBy: 'que_manager' })} /> QUE Manager
                  </label>
                  <label className="glass-radio">
                    <input type="radio" name="assignedBy" value="bot" checked={assignForm.assignedBy === 'bot'}
                      onChange={() => setAssignForm({ ...assignForm, assignedBy: 'bot' })} /> BOT
                  </label>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Case Type</label>
                <div className="flex-gap-4" style={{ flexWrap: 'wrap' }}>
                  {['fresh_case', 'received_from_lob', 'expired_sla'].map((type) => (
                    <label key={type} className="glass-checkbox">
                      <input type="checkbox" checked={assignForm.caseType.includes(type)}
                        onChange={() => toggleCaseType(type)} />
                      {type.replace(/_/g, ' ')}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <input className="glass-input" value={assignForm.notes}
                  onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} />
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setAssignModal(false)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={submitAssignment}
                disabled={actionLoading || !assignForm.caseNumber || assignForm.caseType.length === 0}>
                {actionLoading ? 'Assigning...' : 'Add to Active Cases'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {transferModal && (
        <div className="glass-modal-overlay" onClick={() => setTransferModal(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">↗️ Transfer Case</span>
              <button className="glass-modal__close" onClick={() => setTransferModal(false)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Case Number</label>
                <input className="glass-input" value={transferForm.caseNumber}
                  onChange={(e) => setTransferForm({ ...transferForm, caseNumber: e.target.value })} autoFocus />
              </div>
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">SAP Reference</label>
                <input className="glass-input" value={transferForm.sapReference}
                  onChange={(e) => setTransferForm({ ...transferForm, sapReference: e.target.value })} />
              </div>
              <div className="flex-gap-4" style={{ marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
                <label className="glass-checkbox">
                  <input type="checkbox" checked={transferForm.smeNoteAdded}
                    onChange={(e) => setTransferForm({ ...transferForm, smeNoteAdded: e.target.checked })} />
                  SME Note Added
                </label>
                <label className="glass-checkbox">
                  <input type="checkbox" checked={transferForm.receivedSameMonth}
                    onChange={(e) => setTransferForm({ ...transferForm, receivedSameMonth: e.target.checked })} />
                  Case received this month (nets IPD)
                </label>
              </div>
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Target LOB</label>
                <select className="glass-select" value={transferForm.targetLOB}
                  onChange={(e) => setTransferForm({ ...transferForm, targetLOB: e.target.value })}>
                  <option value="">Select LOB...</option>
                  <option value="Windows">Windows</option>
                  <option value="Office">Office</option>
                  <option value="Azure">Azure</option>
                  <option value="Dynamics">Dynamics</option>
                  <option value="Security">Security</option>
                  <option value="Intune">Intune</option>
                  <option value="Teams">Teams</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="glass-checkbox" style={{ color: 'var(--warning)' }}>
                  <input type="checkbox" checked={transferForm.confirmed}
                    onChange={(e) => setTransferForm({ ...transferForm, confirmed: e.target.checked })} />
                  I confirm this transfer
                </label>
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setTransferModal(false)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={submitTransfer}
                disabled={actionLoading || !transferForm.caseNumber || !transferForm.targetLOB || !transferForm.confirmed}>
                {actionLoading ? 'Transferring...' : 'Transfer Case'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Survey Modal */}
      {surveyModal && (
        <div className="glass-modal-overlay" onClick={() => setSurveyModal(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">⭐ Log Survey Rating</span>
              <button className="glass-modal__close" onClick={() => setSurveyModal(false)}>✕</button>
            </div>
            <div className="glass-modal__body" style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: 'var(--space-4)', color: 'var(--text-secondary)' }}>How did the customer rate you?</p>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
                <StarRating rating={surveyRating} onChange={setSurveyRating} />
              </div>
              {surveyRating > 0 && (
                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: surveyRating >= 4 ? 'var(--success)' : surveyRating === 3 ? 'var(--warning)' : 'var(--error)' }}>
                  {surveyRating} Star{surveyRating > 1 ? 's' : ''}
                </div>
              )}
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setSurveyModal(false)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={submitSurvey} disabled={surveyRating < 1}>Log Rating</button>
            </div>
          </div>
        </div>
      )}

      {/* Calculator Modal */}
      {calcModal && (
        <div className="glass-modal-overlay" onClick={() => setCalcModal(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">🧮 CSAT Calculator</span>
              <button className="glass-modal__close" onClick={() => setCalcModal(false)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                {[5, 4, 3, 2, 1].map((n) => (
                  <div key={n} className="form-group">
                    <label className="form-label" style={{ textAlign: 'center' }}>★{n}</label>
                    <input className="glass-input" type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" style={{ textAlign: 'center' }}
                      value={(calcInputs as any)[`n${n}`]}
                      onChange={(e) => { setCalcInputs({ ...calcInputs, [`n${n}`]: e.target.value.replace(/[^0-9]/g, '') }); }} />
                  </div>
                ))}
              </div>
              <button className="glass-btn glass-btn--secondary" onClick={calculateCsat} style={{ width: '100%', marginBottom: 'var(--space-4)' }}>
                Calculate
              </button>
              {calcResult && (
                <div className="glass-card" style={{ padding: 'var(--space-4)' }}>
                  <div className="grid-stats">
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Average</div>
                      <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 800 }}>{calcResult.average}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Top Box</div>
                      <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 800, color: 'var(--accent-primary)' }}>{calcResult.topBox}%</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>CSat</div>
                      <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 800, color: 'var(--success)' }}>{calcResult.csat}%</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Set Backlog Modal */}
      {backlogModal && (
        <div className="glass-modal-overlay" onClick={() => setBacklogModal(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">📋 Set Starting Backlog</span>
              <button className="glass-modal__close" onClick={() => setBacklogModal(false)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Month</label>
                <input type="month" className="glass-input" value={backlogForm.monthKey}
                  onChange={(e) => setBacklogForm({ ...backlogForm, monthKey: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Starting Backlog Count</label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" className="glass-input" placeholder="0" value={backlogForm.startingBacklog}
                  onChange={(e) => setBacklogForm({ ...backlogForm, startingBacklog: e.target.value.replace(/[^0-9]/g, '') })} />
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setBacklogModal(false)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={setStartingBacklog}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Correct CSAT Modal */}
      {correctCsatModal && (
        <div className="glass-modal-overlay" onClick={() => setCorrectCsatModal(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">📊 Correct CSAT Score</span>
              <button className="glass-modal__close" onClick={() => setCorrectCsatModal(false)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)' }}>
                This will replace all individual ratings with manual counts.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                {[5, 4, 3, 2, 1].map((n) => (
                  <div key={n} className="form-group">
                    <label className="form-label" style={{ textAlign: 'center' }}>★{n}</label>
                    <input className="glass-input" type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" style={{ textAlign: 'center' }}
                      value={(csatCorrectForm as any)[`n${n}`]}
                      onChange={(e) => setCsatCorrectForm({ ...csatCorrectForm, [`n${n}`]: e.target.value.replace(/[^0-9]/g, '') })} />
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setCorrectCsatModal(false)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={correctCsat}>Apply Correction</button>
            </div>
          </div>
        </div>
      )}

      {/* Load / Reset Backlog Modal */}
      {loadBacklogModal && (
        <div className="glass-modal-overlay" onClick={() => setLoadBacklogModal(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">{isResetMode ? '🔄 Reset Backlog' : '📦 Load Backlog'}</span>
              <button className="glass-modal__close" onClick={() => setLoadBacklogModal(false)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div className="form-row" style={{ marginBottom: 'var(--space-3)' }}>
                <div className="form-group">
                  <label className="form-label">Starting Backlog</label>
                  <input className="glass-input" type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" value={loadBacklogForm.startingBacklog}
                    onChange={(e) => setLoadBacklogForm({ ...loadBacklogForm, startingBacklog: e.target.value.replace(/[^0-9]/g, '') })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Cases Assigned</label>
                  <input className="glass-input" type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" value={loadBacklogForm.casesAssigned}
                    onChange={(e) => setLoadBacklogForm({ ...loadBacklogForm, casesAssigned: e.target.value.replace(/[^0-9]/g, '') })} />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
                <label className="form-label">Cases Closed</label>
                <input className="glass-input" type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" value={loadBacklogForm.casesClosed}
                  onChange={(e) => setLoadBacklogForm({ ...loadBacklogForm, casesClosed: e.target.value.replace(/[^0-9]/g, '') })} />
              </div>

              <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
                Transfers
              </h4>
              {loadBacklogForm.transfers.map((t, i) => (
                <div key={i} className="form-row" style={{ marginBottom: 'var(--space-2)', alignItems: 'end' }}>
                  <div className="form-group">
                    <label className="form-label">Count</label>
                    <input className="glass-input" type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" value={t.count}
                      onChange={(e) => {
                        const newT = [...loadBacklogForm.transfers];
                        newT[i] = { ...newT[i], count: e.target.value.replace(/[^0-9]/g, '') };
                        setLoadBacklogForm({ ...loadBacklogForm, transfers: newT });
                      }} />
                  </div>
                  <label className="glass-checkbox" style={{ paddingBottom: 'var(--space-3)' }}>
                    <input type="checkbox" checked={t.receivedSameMonth}
                      onChange={(e) => {
                        const newT = [...loadBacklogForm.transfers];
                        newT[i] = { ...newT[i], receivedSameMonth: e.target.checked };
                        setLoadBacklogForm({ ...loadBacklogForm, transfers: newT });
                      }} />
                    Received same month
                  </label>
                </div>
              ))}
              <button className="glass-btn glass-btn--ghost glass-btn--sm"
                onClick={() => setLoadBacklogForm({ ...loadBacklogForm, transfers: [...loadBacklogForm.transfers, { count: '', receivedSameMonth: false }] })}>
                + Add Transfer Entry
              </button>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setLoadBacklogModal(false)}>Cancel</button>
              <button className={`glass-btn glass-btn--${isResetMode ? 'critical' : 'primary'}`} onClick={loadBacklog} disabled={actionLoading}>
                {actionLoading ? 'Loading...' : isResetMode ? 'Reset Backlog' : 'Load Backlog'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Archive Modal */}
      {archiveModal && (
        <div className="glass-modal-overlay" onClick={() => setArchiveModal(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">📦 Archive — Action Log</span>
              <button className="glass-modal__close" onClick={() => setArchiveModal(false)}>✕</button>
            </div>
            <div className="glass-modal__body">
              {/* Month / Year selector */}
              <div className="flex-gap-3" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Month</label>
                  <select className="glass-select" value={archiveMonth}
                    onChange={(e) => setArchiveMonth(Number(e.target.value))}>
                    {['January','February','March','April','May','June','July','August','September','October','November','December']
                      .map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Year</label>
                  <select className="glass-select" value={archiveYear}
                    onChange={(e) => setArchiveYear(Number(e.target.value))}>
                    {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button className="glass-btn glass-btn--secondary" onClick={loadArchive}>Load</button>
                </div>
              </div>

              {archiveLoading ? (
                <div className="flex-center" style={{ padding: 'var(--space-8)' }}><div className="spinner" /></div>
              ) : archiveActions.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state__icon">📦</div>
                  <div className="empty-state__title">No actions this period</div>
                </div>
              ) : (
                <div className="glass-table-wrapper">
                  <table className="glass-table">
                    <thead><tr>
                      <th>Date</th><th>Case #</th><th>Action</th><th>Details</th><th>Mark</th>
                    </tr></thead>
                    <tbody>
                      {archiveActions.map((a) => (
                        <tr key={a.id}>
                          <td data-label="Date" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                            {new Date(a.createdAt).toLocaleDateString()}
                          </td>
                          <td data-label="Case #" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}>
                            {a.caseNumber}
                          </td>
                          <td data-label="Action">
                            <span className={`glass-badge glass-badge--${
                              a.actionType === 'closure' ? 'resolved' :
                              a.actionType === 'transfer' ? 'in_progress' :
                              a.actionType === 'assignment' ? 'open' :
                              a.actionType === 'customer_reopened' ? 'high' : 'medium'
                            }`}>
                              {a.actionType.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td data-label="Details" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                            {a.closureType?.replace(/_/g, ' ') || a.targetLOB || '—'}
                          </td>
                          <td data-label="Mark">
                            {a.actionType === 'closure' && (
                              <button className="glass-btn glass-btn--ghost glass-btn--sm"
                                style={{ color: 'var(--warning)', fontSize: '11px' }}
                                onClick={() => { submitReopen(a.caseNumber); }}>
                                👤 Reopened
                              </button>
                            )}
                            {a.actionType === 'transfer' && (
                              <button className="glass-btn glass-btn--ghost glass-btn--sm"
                                style={{ color: 'var(--error)', fontSize: '11px' }}
                                onClick={() => { submitBounceBack(a.caseNumber); }}>
                                🔄 Bounced Back
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setArchiveModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
